import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar
} from 'recharts';
import api from '@/services/authService';

interface ReportData {
  dailyStats: Array<{ date: string; users: number; transactions: number; volume: number }>;
  userGrowth: Array<{ month: string; newUsers: number; totalUsers: number }>;
  transactionVolume: Array<{ month: string; transactions: number; amount: number }>;
  currencyDistribution: Array<{ currency: string; count: number; percentage: number }>;
  topUsers: Array<{ username: string; email: string; totalTransactions: number; totalAmount: number }>;
  systemMetrics: {
    totalUsers: number;
    activeUsers: number;
    totalTransactions: number;
    totalVolume: number;
    averageTransaction: number;
    successRate: number;
  };
}

const COLORS = ['#1e3a8a', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const AnalyticsDashboard: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('30');
  const [reportType, setReportType] = useState('overview');

  useEffect(() => {
    fetchReportData();
  }, [dateRange, reportType]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Fetch live analytics data from the API
      const response = await api.get(`/admin/analytics?days=${dateRange}`);

      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError('Failed to fetch analytics data');
      }

    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    // Implement export functionality
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Date,Users,Transactions,Volume\n" +
      reportData?.dailyStats.map(row => 
        `${row.date},${row.users},${row.transactions},${row.volume}`
      ).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `banking_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Analytics & Reports</Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              label="Report Type"
              onChange={(e) => setReportType(e.target.value)}
            >
              <MenuItem value="overview">Overview</MenuItem>
              <MenuItem value="users">Users</MenuItem>
              <MenuItem value="transactions">Transactions</MenuItem>
              <MenuItem value="financial">Financial</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchReportData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportReport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {reportData.systemMetrics.totalUsers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Total Users</Typography>
                </Box>
                <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {reportData.systemMetrics.activeUsers} active
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="secondary">
                    {reportData.systemMetrics.totalTransactions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Transactions</Typography>
                </Box>
                <TimelineIcon sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {reportData.systemMetrics.successRate}% success rate
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    €{((reportData.systemMetrics.totalVolume / 1000000).toFixed(1))}M
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Total Volume</Typography>
                </Box>
                <BarChartIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  €{reportData.systemMetrics.averageTransaction} avg transaction
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {reportData.systemMetrics.successRate}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Success Rate</Typography>
                </Box>
                <PieChartIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
              </Box>
              <Box display="flex" alignItems="center" mt={1}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  Excellent performance
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Daily Activity Trends</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="users" stroke="#1e3a8a" strokeWidth={2} name="Users" />
                  <Line type="monotone" dataKey="transactions" stroke="#10b981" strokeWidth={2} name="Transactions" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Currency Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={reportData.currencyDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                  >
                    {reportData.currencyDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
              <Box display="flex" justifyContent="center" mt={2}>
                {reportData.currencyDistribution.map((entry) => (
                  <Box key={entry.currency} display="flex" alignItems="center" mx={1}>
                    <Box width={12} height={12} bgcolor={COLORS[reportData.currencyDistribution.indexOf(entry)]} borderRadius={2} mr={1} />
                    <Typography variant="caption">{entry.currency} ({entry.percentage}%)</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>User Growth</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={reportData.userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="newUsers" fill="#1e3a8a" name="New Users" />
                  <Bar dataKey="totalUsers" fill="#10b981" name="Total Users" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Transaction Volume</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={reportData.transactionVolume}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="transactions" fill="#f59e0b" name="Transactions" />
                  <Bar dataKey="amount" fill="#ef4444" name="Amount (€)" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Users by Activity</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Total Transactions</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.topUsers.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight="bold">{user.username}</Typography>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell align="right">{user.totalTransactions}</TableCell>
                        <TableCell align="right">€{user.totalAmount.toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;